import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { animDuration, delay, withLoading } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { getAdContinueMoves } from 'src/game/ads/ads.util';
import Match2PinchMovesIcon from './Match2PinchMovesIcon';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { showRewardedAd } from 'src/game/ads/ads.sequence';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: { watchedAds: number }) => void;

const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2AdContinueStreak extends PopupBasic {
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;
  // The popup shows after the first ad watch
  private watchedAds = 1;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close({ watchedAds: this.watchedAds }),
      ...ui.popup,
      width: 630,
      height: ui.popup.height - 130,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.AD_CONTINUE_STREAK.POPUP,
      },
    });

    this.message.removeFromSuperview();
    this.title.updateOpts({ size: 30 });
    this.bg.onClick = () => this.toggleShowPuzzle();

    const container = new FlexBoxView({
      superview: this.box,
      x: 20,
      y: 70,
      width: this.box.style.width - 40,
      height: 200,
      flex: {
        alignItems: 'center',
        justifyContent: 'space-around',
      },
    });

    const streakItems: { item: ImageScaleView; check: ImageView }[] = [];
    for (let i = 0; i < 3; i++) {
      const item = this.createAdStreakPanel(container, i, i < 2);
      streakItems.push(item);
    }

    const updateStreakItems = () => {
      for (let i = 0; i < streakItems.length; i++) {
        const { item, check } = streakItems[i];
        check.updateOpts({ visible: i < this.watchedAds });
        item.updateOpts({
          scale: i === this.watchedAds ? 1.1 : 1,
          opacity: i <= this.watchedAds ? 1 : 0.7,
        });
      }
    };

    updateStreakItems();

    new LangBitmapFontTextView({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: ui.okButton.y - 196,
      width: this.box.style.width,
      height: 100,
      size: 30,
      localeText: () => i18n('match2.adContinue.adInfo'),
      font: 'Body',
      color: '#BF8147',
      centerOnOrigin: true,
      verticalAlign: 'center',
      align: 'center',
    });

    // Ad button
    const adButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.blue,
      x: this.box.style.width * 0.5 - 305 * 0.5,
      y: ui.okButton.y - 150,
      width: 305,
      height: 90,
      fontSize: 40,
      labelOffsetY: -2,
      labelOffsetX: 60,
      labelWidth: 240,
      font: 'Body',
      icon: {
        image: 'assets/ui/shared/icons/icon_ad.png',
        width: 60,
        height: 60,
        offset: {
          x: -110,
          y: -2,
        },
      },
      superview: this.box,
      localeText: () => i18n('match2.adContinue.adCTA'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.AD_CONTINUE_STREAK.WATCH,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        const watchedAd = await withLoading(() => showRewardedAd(), {
          feature: Feature.MATCH2._,
          subFeature: Feature.MATCH2.WACTH_REWARDED_AD,
        });

        if (watchedAd) {
          this.watchedAds++;
          updateStreakItems();

          if (this.watchedAds === 3) {
            adButton.hide();
            closeButton.show();
          }
        }
      },
    });

    const closeButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      x: this.box.style.width * 0.5 - 265 * 0.5,
      y: ui.okButton.y - 150,
      width: 265,
      superview: this.box,
      localeText: () => i18n('match2.adContinue.playOn'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.AD_CONTINUE_STREAK.CLOSE,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close({ watchedAds: this.watchedAds });
      },
      visible: false,
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/shared/icons/icon_ad.png',
      'assets/ui/match2/common/panel_pinch.png',
      'assets/ui/shared/icons/checkmark.png',
      'assets/ui/shared/icons/arrow_right.png',
    ]);
  }

  public init() {
    super.init({ title: i18n('match2.adContinue.title') });

    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 100 + this.box.style.y + this.box.style.height * 0.5 + 100,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;
    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    super.fadeIn();
    await delay(animDuration * 2);
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
    await delay(animDuration * 2);
  }

  private createAdStreakPanel(
    container: FlexBoxView,
    continueIndex: number,
    withArrow: boolean,
  ) {
    const bg = new ImageScaleView({
      superview: container,
      width: 160,
      height: 160,
      image: 'assets/ui/match2/common/panel_pinch.png',
      scaleMethod: '9slice',
      centerAnchor: true,
      sourceSlices: {
        horizontal: { left: 50, right: 50 },
        vertical: { top: 50, bottom: 50 },
      },
    });

    const check = new ImageView({
      superview: bg,
      canHandleEvents: false,
      image: 'assets/ui/shared/icons/checkmark.png',
      width: 56,
      height: 46,
      centerOnOrigin: true,
      x: bg.style.width - 20,
      y: bg.style.height - 20,
      visible: false,
    });

    new Match2PinchMovesIcon({
      superview: bg,
      amount: getAdContinueMoves(continueIndex),
      x: bg.style.width * 0.5,
      y: bg.style.height * 0.5 - 4,
    });

    if (withArrow) {
      new ImageView({
        superview: container,
        image: 'assets/ui/shared/icons/arrow_right.png',
        width: 21,
        height: 40,
      });
    }

    return {
      item: bg,
      check,
    };
  }
}
